// The My Desk canvas — Request Ledger N-090b (owner, 2026-09-08), replacing
// N-090's arrange mode:
//   "This canvas is terrible to manage and not very intuitive. I was
//   thinking we can simply make boxes bigger horizontally and vertically."
//
// Renders one `layout` (client/src/desk/panelCatalog.tsx's DeskLayout) as a
// 12-column grid, ALWAYS directly manipulable — there is no separate
// "arrange mode" to step into any more:
//   - drag a panel's own title bar to MOVE it (client/src/components ->
//     the bar this file renders, not a component's own heading)
//   - drag one of its three edge/corner grips to RESIZE it
//   - hover a panel to reveal its "×" remove button
//   - focus a panel's title bar and use arrow keys / shift+arrow keys to do
//     the same two things without a pointer
// "Add panel" and "Reset layout" moved OUT of this file entirely — they no
// longer need arrange mode as a gate, so they now live in the page header,
// in DeskAddMenu.tsx, which HomePage renders beside this grid.
//
// No external drag/resize library — pointer events (`pointerdown` on the
// title bar or a grip, `pointermove`/`pointerup` on `window`, so a drag
// keeps tracking even once the pointer leaves the small element it started
// on) plus CSS grid. All the actual grid math (packing, overlap resolution,
// pixel-delta-to-cell-delta, size snapping) lives in
// client/src/desk/gridPack.ts as pure functions — this file only wires DOM
// events to them and re-renders.
//
// N-090b judgment call (recorded in DECISIONS.md): every panel's CELL now
// always renders, even one with nothing to show right now — N-090's
// "arrange mode shows empty panels, view mode hides them" distinction had
// no third state to fall into once arrange mode was removed, and hiding a
// panel outright would mean it could pop in and out of the layout as its
// own content changes, and could never be grabbed to move/resize/remove
// while empty. INV-9 ("never a blank that reads as broken") is kept by a
// different mechanism now: an empty panel still shows its OWN title bar
// (this file's, always rendered) and a labelled, TYPE-SPECIFIC "nothing to
// show" placeholder (EmptyState, catalog entry's own `emptyLabel`) where its
// content would have been — never an unlabelled blank rectangle, and never
// the same generic sentence regardless of what the panel was even for.
//
// N-090c (owner, 2026-09-08 — "Every panel shows its name twice"): this
// title bar is now THE one heading a panel gets on the canvas — name, the
// catalog's own muted description, the panel's own action controls (a
// clinic picker, or `entry.renderActions` for anything else, e.g. Canvas's
// Add/Remove selects) right-aligned, "×" at far right on hover, all in one
// bar. Every catalog `render()` now passes its component `chromeless: true`
// (panelCatalog.tsx) so that component's OWN heading — DeskPanel.tsx's or
// PinnedBoard.tsx's `.section-head`, previously a SECOND heading stacked
// directly under this bar — never renders at all. A type may also set
// `minH` (today only the four-clinic strip) — the shortest height a
// drag/keyboard resize may ever leave it at, because its own content
// visibly clipped at the shipped one-row default.
import type { JSX } from 'preact';
import { useRef, useState } from 'preact/hooks';
import type { Role } from '../lib/nav';
import { visibleClinics } from './ClinicTabs';
import { LOCATION_NAMES } from '../lib/locations';
import { EmptyState } from './FaultState';
import {
  catalogEntry, panelKey, type DeskLayout, type DeskPanelInstance, visiblePanelsFor,
} from '../desk/panelCatalog';
import {
  resolveOverlaps, pxDeltaToCellDelta, clampCol, clampRow,
  clampWidthValue, clampHeightValue, snapWidth, snapHeight, stepWidth, stepHeight,
} from '../desk/gridPack';

const ROW_PX = 120;
const GAP_PX = 18;

/** While a move/resize drag is in progress, pin the cursor and suppress
 * text selection at the document level — the pointer can slide off the
 * thin title bar or 8px grip mid-drag (a fast flick, a big monitor), and
 * without this the cursor would revert to the default arrow and any text
 * the drag passes over would highlight, even though the drag itself (driven
 * off `window` pointermove/pointerup) keeps tracking correctly either way.
 * Returns the restore function. */
function beginBodyDragStyle(cursor: string): () => void {
  const { style } = document.body;
  const prevCursor = style.cursor;
  const prevUserSelect = style.userSelect;
  style.cursor = cursor;
  style.userSelect = 'none';
  return () => {
    style.cursor = prevCursor;
    style.userSelect = prevUserSelect;
  };
}

/** What edge(s) of a panel a resize grip controls. */
type ResizeEdge = 'e' | 's' | 'se';

/** Live preview of a move or a resize in progress — always the full
 * rectangle, even though a move only ever changes col/row and a resize only
 * ever changes w/h, so the render below has one shape to read regardless of
 * which is in flight. */
interface Ghost {
  id: string;
  col: number;
  row: number;
  w: number;
  h: number;
}

export interface DeskGridProps {
  layout: DeskLayout;
  role: Role;
  onChange: (layout: DeskLayout) => void;
  /** Owner, 2026-10-12 (desk templates, "LOCK DESK"): a locked Desk — a
   * manager's or director's while Tomer has it locked, or a template he is
   * looking at outside Edit layout. Nothing moves, resizes or is removed,
   * and no clinic picker; the (i) still works. */
  readOnly?: boolean;
}

export function DeskGrid({
  layout, role, onChange, readOnly = false,
}: DeskGridProps): JSX.Element {
  const gridRef = useRef<HTMLDivElement>(null);
  const [ghost, setGhost] = useState<Ghost | null>(null);
  // The panel currently mid-move or mid-resize, purely for the "subtle
  // outline" the build spec asks for while dragging — the actual live
  // position/size comes from `ghost` above.
  const [activeId, setActiveId] = useState<string | null>(null);
  // Which panel's (i) explanation is open (owner, 2026-10-12).
  const [infoOpen, setInfoOpen] = useState<string | null>(null);

  const clinics = visibleClinics();
  const panels = visiblePanelsFor(layout, role);

  function commit(nextPanels: DeskPanelInstance[]): void {
    onChange({ version: 1, panels: nextPanels });
  }

  /** Apply `patch` to one panel, then push anything it now overlaps out of
   * the way — the changed panel goes FIRST into resolveOverlaps so
   * everything else yields to it, never the reverse. Every move, resize,
   * and clinic change goes through this one function. */
  function updatePanel(id: string, patch: Partial<DeskPanelInstance>): void {
    const current = panels.find((p) => p.id === id);
    if (!current || readOnly) return;
    const updated: DeskPanelInstance = { ...current, ...patch };
    const rest = panels.filter((p) => p.id !== id);
    commit(resolveOverlaps([updated, ...rest]));
  }

  function setClinic(panel: DeskPanelInstance, clinic: string): void {
    updatePanel(panel.id, { config: { ...panel.config, clinic } });
  }

  function removePanel(id: string): void {
    // Owner, 2026-10-12: the five always-on panels are never removed.
    if (readOnly) return;
    const type = panels.find((p) => p.id === id)?.type ?? '';
    if (catalogEntry(type)?.permanent) return;
    commit(panels.filter((p) => p.id !== id));
  }

  /** True when ANOTHER panel of the same type already shows `clinic` —
   * the clinic picker greys that clinic out, so a clinic change can never
   * make a duplicate (owner, 2026-10-12). */
  function takenByOther(panel: DeskPanelInstance, clinic: string): boolean {
    const key = panelKey({ type: panel.type, config: { clinic } });
    return panels.some((p) => p.id !== panel.id && panelKey(p) === key);
  }

  // --- move: drag the panel's own title bar ------------------------------

  function beginMove(panel: DeskPanelInstance, ev: PointerEvent): void {
    // A plain click on the bar (no move) must not start dragging for
    // anything but the primary button.
    if (ev.button !== undefined && ev.button !== 0) return;
    if (readOnly) return;
    ev.preventDefault();
    const gridEl = gridRef.current;
    if (!gridEl) return;
    const rect = gridEl.getBoundingClientRect();
    const colStep = (rect.width - 11 * GAP_PX) / 12 + GAP_PX;
    const rowStep = ROW_PX + GAP_PX;
    const startX = ev.clientX;
    const startY = ev.clientY;
    const { col: startCol, row: startRow } = panel;

    setActiveId(panel.id);
    const restoreBodyStyle = beginBodyDragStyle('grabbing');

    function nextPos(moveEv: PointerEvent): { col: number; row: number } {
      const { dCol, dRow } = pxDeltaToCellDelta(
        moveEv.clientX - startX, moveEv.clientY - startY, colStep, rowStep
      );
      return {
        col: clampCol(startCol + dCol, panel.w),
        row: clampRow(startRow + dRow),
      };
    }

    function onMove(moveEv: PointerEvent): void {
      const pos = nextPos(moveEv);
      setGhost({
        id: panel.id, w: panel.w, h: panel.h, ...pos,
      });
    }

    function onUp(upEv: PointerEvent): void {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      restoreBodyStyle();
      const pos = nextPos(upEv);
      setActiveId(null);
      setGhost(null);
      if (pos.col !== panel.col || pos.row !== panel.row) {
        updatePanel(panel.id, pos);
      }
    }

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  // --- resize: drag one of the three edge/corner grips --------------------

  function beginResize(panel: DeskPanelInstance, edge: ResizeEdge, ev: PointerEvent): void {
    if (ev.button !== undefined && ev.button !== 0) return;
    ev.preventDefault();
    ev.stopPropagation(); // never also start a move
    if (readOnly) return;
    const gridEl = gridRef.current;
    if (!gridEl) return;
    const rect = gridEl.getBoundingClientRect();
    const colStep = (rect.width - 11 * GAP_PX) / 12 + GAP_PX;
    const rowStep = ROW_PX + GAP_PX;
    const startX = ev.clientX;
    const startY = ev.clientY;
    const {
      w: startW, h: startH, col, row,
    } = panel;
    // N-090c: a panel's TYPE may set a floor below which it visibly clips
    // (today only the four-clinic strip, at 2 row units) — a resize must
    // never drag or snap it back under that, the same way `lockWidth`
    // already keeps a resize from touching this type's width at all.
    const minH = catalogEntry(panel.type)?.minH ?? 1;

    setActiveId(panel.id);
    const cursor = edge === 'e' ? 'ew-resize' : edge === 's' ? 'ns-resize' : 'nwse-resize';
    const restoreBodyStyle = beginBodyDragStyle(cursor);

    // LIVE size — grows/shrinks one column or row unit at a time, not yet
    // snapped to the six width fractions / four height units (see
    // gridPack.ts's clampWidthValue/clampHeightValue). Snapping happens
    // once, on release.
    function liveSize(moveEv: PointerEvent): { w: number; h: number } {
      const { dCol, dRow } = pxDeltaToCellDelta(
        moveEv.clientX - startX, moveEv.clientY - startY, colStep, rowStep
      );
      const w = edge === 's' ? startW : clampWidthValue(startW + dCol, col);
      const h = edge === 'e' ? startH : Math.max(minH, clampHeightValue(startH + dRow));
      return { w, h };
    }

    function onMove(moveEv: PointerEvent): void {
      const size = liveSize(moveEv);
      setGhost({
        id: panel.id, col, row, ...size,
      });
    }

    function onUp(upEv: PointerEvent): void {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      restoreBodyStyle();
      const raw = liveSize(upEv);
      const snapped: Pick<DeskPanelInstance, 'w' | 'h'> = {
        w: (edge === 's' ? startW : snapWidth(raw.w, col)) as DeskPanelInstance['w'],
        h: (edge === 'e' ? startH : Math.max(minH, snapHeight(raw.h))) as DeskPanelInstance['h'],
      };
      setActiveId(null);
      setGhost(null);
      if (snapped.w !== panel.w || snapped.h !== panel.h) {
        updatePanel(panel.id, snapped);
      }
    }

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  // --- keyboard fallback: title bar focused, arrow to move, shift+arrow to
  // resize one step (build spec item 6 — small, but real: a canvas whose
  // only interaction is a mouse drag has no keyboard path at all). ---------

  function handleKeyDown(
    panel: DeskPanelInstance,
    lockWidth: boolean,
    ev: KeyboardEvent,
  ): void {
    const { key } = ev;
    if (key !== 'ArrowUp' && key !== 'ArrowDown' && key !== 'ArrowLeft' && key !== 'ArrowRight') return;
    // A locked Desk ignores the keyboard (owner, 2026-10-12) — arrows keep
    // their normal page-scroll meaning there.
    if (readOnly) return;
    ev.preventDefault();

    if (ev.shiftKey) {
      const minH = catalogEntry(panel.type)?.minH ?? 1;
      const patch: Partial<DeskPanelInstance> = {};
      if (key === 'ArrowRight' && !lockWidth) patch.w = snapWidth(stepWidth(panel.w, 1), panel.col) as DeskPanelInstance['w'];
      else if (key === 'ArrowLeft' && !lockWidth) patch.w = snapWidth(stepWidth(panel.w, -1), panel.col) as DeskPanelInstance['w'];
      else if (key === 'ArrowDown') patch.h = stepHeight(panel.h, 1) as DeskPanelInstance['h'];
      // Never step below this type's own floor (see beginResize's matching
      // comment) — a shrink that would cross it just stops there instead of
      // going one step further and clipping the panel's own content again.
      else if (key === 'ArrowUp') patch.h = Math.max(minH, stepHeight(panel.h, -1)) as DeskPanelInstance['h'];
      const changed = (patch.w !== undefined && patch.w !== panel.w)
        || (patch.h !== undefined && patch.h !== panel.h);
      if (changed) updatePanel(panel.id, patch);
      return;
    }

    let col = panel.col;
    let row = panel.row;
    if (key === 'ArrowRight') col = clampCol(panel.col + 1, panel.w);
    else if (key === 'ArrowLeft') col = clampCol(panel.col - 1, panel.w);
    else if (key === 'ArrowDown') row = clampRow(panel.row + 1);
    else if (key === 'ArrowUp') row = clampRow(panel.row - 1);
    if (col !== panel.col || row !== panel.row) updatePanel(panel.id, { col, row });
  }

  return (
    <div class="desk-layout" ref={gridRef} data-testid="desk-grid">
      {panels.map((panel) => {
        const entry = catalogEntry(panel.type);
        // A saved layout may carry a panel type that no longer exists (Pay & Hours,
        // Tasks … removed 2026-09-08): skip it instead of crashing the whole desk.
        if (!entry) return null;
        // See the module comment: `hasContent` no longer decides whether the
        // CELL renders (it always does) — only whether the content area
        // shows the real panel or a labelled "nothing to show" placeholder.
        const visible = entry.hasContent ? entry.hasContent(panel.config) : true;
        const content = entry.render(panel.config);
        const lockWidth = Boolean(entry.lockWidth);
        // Owner, 2026-10-12: a locked Desk — no drag, no grips, no keyboard
        // move/resize, no remove. The (i) still works.
        const fixed = readOnly;

        const pos = ghost && ghost.id === panel.id ? ghost : panel;
        const style: JSX.CSSProperties = {
          gridColumn: `${pos.col + 1} / span ${pos.w}`,
          gridRow: `${pos.row + 1} / span ${pos.h}`,
        };

        return (
          <div
            key={panel.id}
            class={`desk-grid-cell${activeId === panel.id ? ' desk-live-edit' : ''}${fixed ? ' desk-grid-cell-fixed' : ''}`}
            style={style}
            data-testid={`desk-panel-${panel.id}`}
            data-panel-type={panel.type}
            data-fixed={fixed ? 'true' : undefined}
          >
            <div
              class={`desk-panel-titlebar${fixed ? ' desk-panel-titlebar-fixed' : ''}`}
              tabIndex={fixed ? undefined : 0}
              role="group"
              aria-label={fixed
                ? `${entry.label} — fixed in place`
                : `${entry.label} — drag to move; arrow keys move, shift+arrow keys resize`}
              data-testid={`desk-titlebar-${panel.id}`}
              onPointerDown={fixed ? undefined : (ev) => beginMove(panel, ev as unknown as PointerEvent)}
              onKeyDown={fixed ? undefined : (ev) => handleKeyDown(panel, lockWidth, ev as unknown as KeyboardEvent)}
            >
              <span class="desk-panel-titlebar-names">
                <span class="desk-panel-titlebar-label">{entry.labelFor ? entry.labelFor() : entry.label}</span>
                {/* Owner, 2026-10-12: no description on the panel itself — a
                    small (i) shows it when clicked. */}
                {entry.description ? (
                  <button
                    type="button"
                    class="desk-panel-info"
                    aria-label={`What is ${entry.label}?`}
                    aria-expanded={infoOpen === panel.id}
                    data-testid={`desk-info-${panel.id}`}
                    onPointerDown={(ev) => ev.stopPropagation()}
                    onClick={() => setInfoOpen(infoOpen === panel.id ? null : panel.id)}
                  >
                    i
                  </button>
                ) : null}
              </span>
              {entry.clinicConfigurable && !readOnly && role !== 'MANAGER' && clinics.length > 1 ? (
                <select
                  class="desk-canvas-add desk-panel-titlebar-clinic"
                  aria-label={`${entry.label} clinic`}
                  data-testid={`desk-clinic-${panel.id}`}
                  value={panel.config?.clinic ?? ''}
                  onPointerDown={(ev) => ev.stopPropagation()}
                  onChange={(ev) => setClinic(panel, (ev.target as HTMLSelectElement).value)}
                >
                  {entry.allClinics ? <option value="all" disabled={takenByOther(panel, 'all')}>All clinics</option> : null}
                  {clinics.map((c) => (
                    <option key={c} value={c} disabled={takenByOther(panel, c)}>{LOCATION_NAMES[c]}</option>
                  ))}
                </select>
              ) : null}
              {entry.renderActions ? entry.renderActions(panel.config) : null}
              {entry.permanent || fixed ? null : (
                <button
                  type="button"
                  class="desk-remove-btn"
                  aria-label={`Remove ${entry.label}`}
                  data-testid={`desk-remove-${panel.id}`}
                  onPointerDown={(ev) => ev.stopPropagation()}
                  onClick={() => removePanel(panel.id)}
                >
                  ×
                </button>
              )}
            </div>
            {infoOpen === panel.id && entry.description ? (
              <p class="desk-panel-info-text" data-testid={`desk-info-text-${panel.id}`}>{entry.description}</p>
            ) : null}
            <div class="desk-grid-cell-content">
              {visible ? content : <EmptyState label={entry.emptyLabel ?? 'Nothing to show right now.'} />}
            </div>
            {!lockWidth && !fixed ? (
              <div
                class="desk-resize-grip desk-resize-e"
                aria-hidden="true"
                data-testid={`desk-resize-e-${panel.id}`}
                onPointerDown={(ev) => beginResize(panel, 'e', ev as unknown as PointerEvent)}
              />
            ) : null}
            {!fixed ? (
              <div
                class="desk-resize-grip desk-resize-s"
                aria-hidden="true"
                data-testid={`desk-resize-s-${panel.id}`}
                onPointerDown={(ev) => beginResize(panel, 's', ev as unknown as PointerEvent)}
              />
            ) : null}
            {!lockWidth && !fixed ? (
              <div
                class="desk-resize-grip desk-resize-se"
                aria-hidden="true"
                data-testid={`desk-resize-se-${panel.id}`}
                onPointerDown={(ev) => beginResize(panel, 'se', ev as unknown as PointerEvent)}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
